<template>
  <v-row no-gutters class="tab-item">
    <v-col>
      <template v-if="!businessAddresses.length">
        <v-row justify="center" align-content="center" class="no-data">
          <v-col sm="6" class="text-center">
            <h3 class="mb-3">
              {{ $t("message.common.addOwnBusinessAddress") }}
            </h3>
            <p>
              {{ $t("message.visibility.summary.noBusinessAddressesInfo") }}
            </p>
            <div class="d-flex flex-column mt-8">
              <ui-button
                icon="icon-google"
                color="primary"
                class="import-btn"
                @click="handleImportBusinessAddresses"
              >
                {{ $t("message.common.importFacilities") }}
              </ui-button>
              <template v-if="canManageCompetitionPlaces">
                <span class="my-2">{{ $t("message.common.or") }}</span>
                <ui-button
                  color="primary"
                  class="import-btn"
                  @click="handleAddNewBusinessAddress"
                >
                  <v-icon small>icon-plus</v-icon
                  >{{ $t("message.common.addBusinessAddress") }}
                </ui-button>
              </template>
            </div>
          </v-col>
        </v-row>
      </template>
      <template v-if="businessAddresses && businessAddresses.length > 0">
        <v-row no-gutters>
          <v-col cols="12" lg="6" offset-lg="3" class="mt-5 text-center">
            <h4 class="mb-4">
              {{
                $t("message.competition.visibility.businessAddresses.cardTitle")
              }}
            </h4>
            <p>
              {{
                $t(
                  "message.competition.visibility.businessAddresses.cardDescription"
                )
              }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="3" order-lg="1" class="px-4">
            <h4>
              {{ $t("message.common.selectAddress") }}
            </h4>
            <v-select
              v-model="selectedBusinessAddress"
              return-object
              item-text="name"
              item-value="place_id"
              @change="fetchDataForSelectedBusinessAddress"
              :items="filteredItems"
              :item-disabled="isItemDisabled"
              class="pt-2"
            >
              <template v-slot:prepend-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-text-field
                      hide-details
                      prepend-icon="icon-search"
                      :placeholder="$t('message.common.findFacility')"
                      full-width
                      v-model="search"
                      single-line
                    >
                    </v-text-field>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template #selection="{item}">
                <v-list-item-content style="flex-basis: 100%;">
                  <v-list-item-title
                    v-html="item.name || item.url"
                  ></v-list-item-title>
                  <v-list-item-subtitle
                    v-html="item.formatted_address"
                  ></v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template #item="{item}">
                <template v-if="typeof item !== 'object'">
                  <v-list-item-content v-text="item"></v-list-item-content>
                </template>
                <template v-else>
                  <v-list-item-content>
                    <v-list-item-title
                      v-html="item.name || item.url"
                    ></v-list-item-title>
                    <v-list-item-subtitle
                      v-html="item.formatted_address"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="isItemDisabled(item)">
                    <v-tooltip max-width="300px" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on" small color="grey"
                          >mdi-information</v-icon
                        >
                      </template>
                      <span>{{
                        $t("message.common.areaFacilityTooltip")
                      }}</span>
                    </v-tooltip>
                  </v-list-item-action>
                </template>
              </template>
            </v-select>
            <div class="select-hint pb-4">
              <span class="select-hint__text grey--text text--darken-1 caption">
                {{
                  $t(
                    "message.competition.visibility.businessAddresses.inputLegend"
                  )
                }}
              </span>
            </div>
            <template v-if="companyCompetition.length > 0">
              <h4 class="mt-5 mb-3">
                {{
                  $t(
                    "message.competition.visibility.businessAddresses.listTitle"
                  )
                }}
                <span class="body-1">(max 5)</span>
              </h4>
              <v-list
                v-if="companyCompetition.length > 0"
                class="business-addresses__list mb-3 overflow-y-auto"
                :height="businessAddressesListHeight"
                two-line
              >
                <v-list-item
                  v-for="(item, index) in companyCompetition"
                  :key="item.id"
                  class="pa-0"
                >
                  <v-tooltip right :max-width="tooltipMaxWidth">
                    <template v-slot:activator="{ on }">
                      <v-list-item-content v-on="on">
                        <div class="d-flex align-center">
                          <v-checkbox
                            v-model="item.model"
                            @change="toggleCheckbox({ item, index })"
                            ref="competitionCheckbox"
                            hide-details
                            class="mx-2 mt-0"
                            :color="item.color"
                            :disabled="checkboxDisabled && !item.color"
                          >
                            <template v-slot:label>
                              <div style="max-width: 250px;">
                                <v-list-item-title class="mb-2">
                                  <span class="body text--primary">{{
                                    item.name
                                  }}</span>
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                  {{ item.formatted_address }}
                                </v-list-item-subtitle>
                              </div>
                            </template>
                          </v-checkbox>
                        </div>
                      </v-list-item-content>
                    </template>
                    <v-list-item-title>
                      <span>{{ item.name }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.formattedAddress }}
                    </v-list-item-subtitle>
                  </v-tooltip>
                  <div v-if="item.color" class="checkbox__number">
                    {{ item.number }}
                  </div>
                </v-list-item>
              </v-list>
              <div v-else class="empty-list">
                {{ $t("message.competition.businessAddresses.noData") }}
              </div>
              <div class="d-flex justify-center">
                <ui-button
                  v-if="canManageCompetitionPlaces"
                  @click="handleAddCompetitionBusinessAddress"
                  icon="icon-plus"
                  color="primary"
                >
                  {{
                    $t(
                      "message.competition.businessAddresses.label.addCompetitionBusinessAddress"
                    )
                  }}
                </ui-button>
              </div>
            </template>
            <template v-else>
              <div class="d-flex flex-column align-center justify-center mt-12">
                <p class="body-2">
                  {{
                    $t(
                      "message.competition.visibility.businessAddresses.listNoData"
                    )
                  }}
                </p>
                <ui-button
                  v-if="canManageCompetitionPlaces"
                  :to="{
                    name: 'competition-business-addresses',
                    query: { itemId: selectedBusinessAddress.id }
                  }"
                  color="primary"
                  icon="icon-plus"
                >
                  {{ $t("message.common.addBusinessAddress") }}
                </ui-button>
              </div>
            </template>
            <SectionLoader :status="loadingCheckbox" />
          </v-col>
          <v-col cols="12" lg="6" order-lg="2">
            <v-card flat>
              <v-overlay v-model="noDataInfo" absolute="absolute">
                <div class="d-flex justify-center align-center">
                  <v-card
                    height="250"
                    width="350"
                    class="d-flex justify-center align-center flex-column pa-4"
                    light
                  >
                    <v-icon x-large color="primary">mdi-database-search</v-icon>
                    <h3 class="mb-3">
                      {{ $t("message.common.noData") }}
                    </h3>
                    <p class="text-center">
                      {{
                        $t(
                          "message.competition.visibility.businessAddresses.searchingData"
                        )
                      }}
                    </p>
                  </v-card>
                </div>
              </v-overlay>
              <div class="relative">
                <Map
                  v-if="center"
                  :center="center"
                  :coords="coords"
                  :defaultStyles="`min-height: ${mapHeight}px; height: 100%;`"
                >
                  <template v-slot:marker v-if="coords.length > 0">
                    <MapMarker
                      v-for="{ icon, id, marker, popupData } in coords"
                      :key="id"
                      :icon="icon"
                      :markerType="marker"
                      :popupData="popupData"
                    />
                    <template v-if="competitionCoords.length > 0">
                      <MapMarker
                        v-for="{
                          icon,
                          id,
                          marker,
                          number,
                          popupData
                        } in competitionCoords"
                        :key="id"
                        :icon="icon"
                        :markerType="marker"
                        :number="number"
                        :popupData="popupData"
                      />
                    </template>
                  </template>
                  <template
                    v-slot:squares
                    v-if="
                      this.selectedBusinessAddress &&
                        this.selectedKeywordId &&
                        this.selectedCompetition.length > 0
                    "
                  >
                    <MapCompetitionLayer
                      v-for="square in competitionMap"
                      :key="square.id"
                      :square="square"
                    />
                  </template>
                </Map>
                <div class="map-dynamic-legend">
                  <template v-for="item in selectedCompetition">
                    <div :key="item.id" class="map-dynamic-legend__item">
                      <v-tooltip right :max-width="tooltipMaxWidth">
                        <template #activator="{ on }">
                          <div
                            v-on="on"
                            class="map-dynamic-legend__box"
                            :class="item.color"
                          >
                            <span class="map-dynamic-legend__name">
                              {{ item.name.charAt(0) }}
                            </span>
                          </div>
                        </template>
                        <v-list-item-content>
                          <v-list-item-title>
                            <h5>{{ item.name }}</h5>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ item.formatted_address }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-tooltip>
                    </div>
                  </template>
                </div>
              </div>
            </v-card>
            <v-alert color="primary" dense outlined class="mb-0 mt-4">
              <span class="caption">
                {{
                  $t(
                    "message.competition.visibility.businessAddresses.cardInfo"
                  )
                }}
              </span>
            </v-alert>
            <SectionLoader :status="loadingMap" />
          </v-col>
          <v-col cols="12" lg="3" order-lg="3">
            <template
              v-if="
                selectedBusinessAddressKeywords.length > 0 &&
                  selectedKeywordId !== null
              "
            >
              <KeywordList
                :minHeight="562"
                :maxHeight="562"
                :selectedId.sync="selectedKeywordId"
                :hasAddButton="false"
                :items="selectedBusinessAddressKeywords"
                :loading="loadingKeywordList"
                @update:selectedId="setKeywordId($event)"
                @loadingKeywords="loadingKeywordList = $event.value"
              >
              </KeywordList>
              <div class="d-flex justify-center">
                <ui-button
                  color="primary"
                  class="mt-4"
                  icon="icon-plus"
                  @click="handleAddNewTerm"
                >
                  {{ $t("message.common.addNewTerm") }}
                </ui-button>
              </div>
            </template>
            <template v-else>
              <v-col
                cols="12"
                class="d-flex justify-center flex-column align-center fill-height"
              >
                <v-icon x-large color="primary">mdi-database-search</v-icon>
                <h3 class="mb-3">
                  {{ $t("message.common.noData") }}
                </h3>
                <p class="text-center">
                  {{
                    $t(
                      "message.competition.visibility.businessAddresses.addNewTerm"
                    )
                  }}
                </p>
                <ui-button
                  color="primary"
                  class="mb-0 mt-4"
                  icon="icon-plus"
                  :to="{
                    name: 'profile-company-in-web',
                    query: { itemId: selectedBusinessAddress.id }
                  }"
                >
                  {{ $t("message.common.addNewTerm") }}
                </ui-button>
              </v-col>
            </template>
            <SectionLoader :status="loadingKeywordList" />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" lg="6" offset-lg="3" class="mt-5 text-center">
            <p class="mb-4">
              {{
                $t(
                  "message.competition.visibility.businessAddresses.chartDescription"
                )
              }}
            </p>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col class="mt-8 p-relative">
            <template v-if="chartData.length > 0 && chartLabels.length > 0">
              <Chart
                :height="500"
                :data="chartData"
                :labels="chartLabels"
                :loading="loadingChart"
                @loadingChartStatus="loadingChart = $event.value"
              />
            </template>
            <template v-else>
              <v-col cols="12">
                <NoData />
              </v-col>
            </template>
            <ChartPagination
              @changeMonth="changeMonth"
              :currentMonth="currentMonth"
            />
            <SectionLoader :status="false" />
            <SectionLoader :status="loadingChart" />
          </v-col>
        </v-row>
      </template>
      <SectionLoader :status="loading" :opacity="1" />
    </v-col>
  </v-row>
</template>

<script>
import Map from "@/components/ui/Map/Map.vue";
import MapMarker from "@/components/ui/Map/Marker.vue";
import MapCompetitionLayer from "@/components/ui/Map/Layers/Competition.vue";
import KeywordList from "@/components/Visibility/KeywordList.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import Chart from "@/components/Competition/BusinessAddresses/Chart.js";
import ChartPagination from "@/components/Chart/Pagination.vue";

import businessAddressesService from "@/services/businessAddresses";
import keywordsService from "@/services/keywords";
import competitionService from "@/services/competition";
import serviceHandler from "@/helpers/handlers/service";
import { gtmEvent__buttonClick } from "@/helpers/gtm-events";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";

import { primary, numbered } from "@/config/marker.js";

import { heatmap } from "@/helpers/mixins/heatmap";

import moment from "moment";
import cuid from "cuid";
import NoData from "@/components/ui/NoData";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { BUSINESS_TYPE } from "@/enums/business-type.enum";
export default {
  name: "CompetitionBusinessAddressesHOC",
  mixins: [heatmap, permissionsInfo],
  components: {
    SectionLoader,
    KeywordList,
    Map,
    MapMarker,
    MapCompetitionLayer,
    Chart,
    ChartPagination,
    NoData
  },

  data() {
    return {
      markerType: { primary, numbered },
      tooltipMaxWidth: 500,
      businessAddressesListHeight: 401,
      mapHeight: 600,
      noDataInfo: false,
      checkboxDisabled: false,
      coords: [],
      competitionCoords: [],
      selectedCompetition: [],
      labelNumbers: [1, 2, 3, 4, 5],
      checkboxColors: ["pink", "purple", "indigo", "cyan", "teal"],
      businessAddresses: [],
      companyCompetition: [],
      loading: false,
      loadingMap: false,
      loadingCheckbox: false,
      loadingKeywordList: false,
      loadingChart: false,
      selectedBusinessAddress: {},
      selectedBusinessAddressKeywords: [],
      selectedKeywordData: [],
      selectedKeywordId: null,
      competitionMap: [],
      chartData: [],
      chartLabels: [],
      currentMonth: {},
      search: ""
    };
  },

  computed: {
    center() {
      if (
        this.selectedBusinessAddress !== undefined &&
        this.selectedBusinessAddress !== null
      ) {
        return this.coords.find(
          item => item.id === this.selectedBusinessAddress.place_id
        );
      }
      return { icon: [52.2200567, 19.2701527], id: cuid() };
    },

    filteredItems() {
      return this.businessAddresses.filter(item => {
        return (
          (item.name && item.name.toLowerCase().match(this.search)) ||
          (item.formatted_address &&
            item.formatted_address.toLowerCase().match(this.search))
        );
      });
    }
  },

  async created() {
    this.loading = true;
    await this.getCurrentMonth();
    await this.fetchBusinessAddresses();
    if (this.businessAddresses.length > 0) {
      this.selectBusinessAddress();
      await this.fetchCompanyCompetitionList();
      await this.fetchKeywords();
      this.selectedKeywordId =
        this.selectedBusinessAddressKeywords &&
        this.selectedBusinessAddressKeywords.length > 0
          ? this.selectedBusinessAddressKeywords[0].id
          : null;
    }
    this.loading = false;
  },

  methods: {
    isItemDisabled(item) {
      return item.business_type === BUSINESS_TYPE.AREA;
    },
    getCurrentMonth(month) {
      month = month || moment();
      this.currentMonth = month;
    },

    async changeMonth(val) {
      this.getCurrentMonth(val);

      if (this.selectedCompetition.length > 0) {
        this.loadingChart = true;
        await this.getChartData();
        this.loadingChart = false;
      }
    },

    async updateUI() {
      const createCompetitionMapPromise = this.createCompetitionMap();
      const chartDataPromise = this.getChartData();

      await Promise.all([createCompetitionMapPromise, chartDataPromise]);
    },

    async toggleCheckbox({ item }) {
      await this.toggleSelectedCompetition(item);
      this.loadingCheckbox = true;
      this.loadingKeywordList = true;
      this.loadingChart = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.COMPETITION_LOCATION_SELECT,
        own_location_id: this.selectedBusinessAddress
          ? this.selectedBusinessAddress.id
          : null,
        competition_locations_ids: this.competitionCoords
          ? this.competitionCoords.map(coord => coord.id).join(", ")
          : null,
        competition_locations_num: this.competitionCoords
          ? this.competitionCoords.length
          : null
      });

      await this.updateUI();

      this.loadingCheckbox = false;
      this.loadingKeywordList = false;
      this.loadingChart = false;
      if (!this.selectedCompetition.length > 0) {
        this.chartData = [];
        this.chartLabels = [];
      }
    },

    setDefaultCompetitionData() {
      this.selectedCompetition = [];
      this.labelNumbers = [1, 2, 3, 4, 5];
      this.checkboxColors = ["pink", "purple", "indigo", "cyan", "teal"];
      this.competitionCoords = [];
      this.checkboxDisabled = false;
      for (const item of this.companyCompetition) {
        item.model = false;
      }
    },

    toggleSelectedCompetition(item) {
      const isStored = this.selectedCompetition.findIndex(
        competition => item.id === competition.id
      );

      if (isStored === -1) {
        item.color = this.checkboxColors[0];
        item.number = this.labelNumbers[0];
        item.model = true;
        this.checkboxColors.splice(0, 1);
        this.labelNumbers.splice(0, 1);

        this.selectedCompetition.push(item);
        this.competitionCoords.push({
          icon: item.geo_coords,
          id: item.place_id,
          marker: this.markerType.numbered,
          number: item.number,
          popupData: {
            name: item.name,
            color: item.color,
            address: item.formatted_address
          }
        });
      } else {
        this.checkboxColors.unshift(item.color);
        this.labelNumbers.unshift(item.number);

        item.color = "";
        item.number = 0;
        item.model = false;

        this.selectedCompetition.splice(isStored, 1);

        const markerIndexToRemove = this.competitionCoords.findIndex(
          competition => competition.id === item.place_id
        );

        if (markerIndexToRemove >= 0) {
          this.competitionCoords.splice(markerIndexToRemove, 1);
        }
      }

      this.checkboxDisabled = this.selectedCompetition.length >= 5;
    },

    async getCoords() {
      this.coords = [
        {
          icon: this.selectedBusinessAddress.geo_coords,
          id: this.selectedBusinessAddress.place_id,
          marker: this.markerType.primary,
          popupData: {
            name: this.selectedBusinessAddress.name,
            color: "orange",
            address: this.selectedBusinessAddress.formatted_address
          }
        }
      ];
    },

    async fetchBusinessAddresses() {
      const data = await serviceHandler(
        businessAddressesService.getBusinessAddressesData()
      );

      if (data.length > 0) {
        this.businessAddresses = data;
      }
    },

    async fetchCompanyCompetitionList() {
      const data = await serviceHandler(
        businessAddressesService.getCompetitionBusinessAddresses(
          this.selectedBusinessAddress.id
        )
      );

      if (data.length > 0) {
        for (const item of data) {
          item.color = "";
        }
      }

      this.companyCompetition = data;
    },

    async fetchDataForSelectedBusinessAddress(location) {
      this.loadingMap = true;
      this.loadingCheckbox = true;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.LOCATIONS_PICKER_SELECT,
        location_id: location.id
      });

      this.setDefaultCompetitionData();
      await this.fetchCompanyCompetitionList();
      await this.fetchKeywords();

      this.chartData = [];
      this.chartLabels = [];

      if (
        this.selectedBusinessAddressKeywords &&
        this.selectedBusinessAddressKeywords.length > 0
      ) {
        this.selectedKeywordId = this.selectedBusinessAddressKeywords[0].id;
        await this.createCompetitionMap();
      }

      this.loadingMap = false;
      this.loadingCheckbox = false;
    },

    async fetchKeywords() {
      this.loadingKeywordList = true;

      const businessAddressId = this.selectedBusinessAddress.id;
      const facility_id = this.selectedBusinessAddress.facility_id;

      this.selectBusinessAddress(businessAddressId);
      const result = await serviceHandler(
        keywordsService.getCompanyKeywords({ facility_id })
      );

      if (result) {
        this.selectedBusinessAddressKeywords = result.map(
          ({ keyword }) => keyword
        );
      }

      this.loadingKeywordList = false;
    },

    selectBusinessAddress(id) {
      if (!id) {
        this.selectedBusinessAddress = this.businessAddresses[0];
        this.getCoords();
        return;
      }

      this.selectedBusinessAddress = this.businessAddresses.find(
        businessAddress => businessAddress.id === id
      );

      this.getCoords();
    },

    async createCompetitionMap() {
      return new Promise(async (resolve, reject) => {
        if (this.selectedCompetition.length === 0) {
          this.$notify({
            group: "default",
            type: "info",
            text: this.$t("message.notification.noCompetitionAdded")
          });
        }
        if (
          this.selectedBusinessAddress &&
          this.selectedKeywordId &&
          this.selectedCompetition.length > 0
        ) {
          const data = await serviceHandler(
            competitionService.getMapCompetitionList({
              businessAddressId: this.selectedBusinessAddress.id,
              keywordId: this.selectedKeywordId,
              competitionIds: this.selectedCompetition.map(item => item.id)
            })
          );

          const businessAddressSquares =
            data[0].business_address.length > 0
              ? this.createSquaresFromHeatPoints(
                  data[0].business_address[0].data
                )
              : null;

          const businessAddressSquaresWithIntensity = businessAddressSquares
            ? {
                data: businessAddressSquares,
                color: "orange",
                intensity: businessAddressSquares.map(square =>
                  this.averageIntensity(square.points)
                )
              }
            : null;

          const competitionBusinessAddressesSquares =
            data[1].business_address_competition.length > 0
              ? data[1].business_address_competition.map(
                  (competition, index) => {
                    const { color } = this.selectedCompetition.find(
                      item => competition.id === item.id
                    );

                    const squares = this.createSquaresFromHeatPoints(
                      competition.data
                    );

                    return {
                      data: squares,
                      color: color,
                      intensity: squares.map(square =>
                        this.averageIntensity(square.points)
                      )
                    };
                  }
                )
              : null;

          const places = [];

          if (businessAddressSquaresWithIntensity)
            places.push(businessAddressSquaresWithIntensity);
          if (competitionBusinessAddressesSquares)
            places.push(...competitionBusinessAddressesSquares);

          this.noDataInfo = !places.length > 0;

          const finalSquares = [];

          if (places.length > 0) {
            for (
              let i = 0;
              i < businessAddressSquaresWithIntensity.data.length;
              i++
            ) {
              let max_intensity = -1;
              let max_color = null;

              for (const place of places) {
                let color = place.color;
                let intensity = place.intensity[i];

                if (intensity > max_intensity) {
                  max_intensity = intensity;
                  max_color = color;
                }
              }

              finalSquares.push({
                points: businessAddressSquaresWithIntensity.data[i].points,
                color: max_color,
                id: businessAddressSquaresWithIntensity.data[i].id,
                strength: max_intensity - places[0].intensity[i]
              });
            }
          }

          this.competitionMap = finalSquares;
        }
        resolve();
      });
    },

    averageIntensity(square) {
      const squareIntensity = square
        .map(point => {
          return point[2];
        })
        .reduce((total, current, index, array) => {
          total += current;
          if (index === array.length - 1) {
            return total / array.length;
          }
          return total;
        }, 0)
        .toFixed(2);

      return squareIntensity;
    },

    async setKeywordId(keywordId) {
      this.selectedKeywordId = keywordId;
      this.loadingCheckbox = true;
      this.loadingKeywordList = true;

      await this.updateUI();

      this.loadingCheckbox = false;
      this.loadingKeywordList = false;
    },

    async getChartData() {
      return new Promise(async (resolve, reject) => {
        const businessAddressData = {
          color: "#f2ae01",
          name: `${this.$t("message.common.yourFacility")}: ${
            this.selectedBusinessAddress.name
          }`,
          data: [],
          address: `${this.selectedBusinessAddress.street}, ${this.selectedBusinessAddress.postal_code} ${this.selectedBusinessAddress.city}`
        };
        const businessAddressLabels = [];
        const competitionData = [];

        const createPositions = arr => {
          const positions = [];
          let dateValue = "";
          arr.forEach(item => {
            if (!positions.length > 0) {
              dateValue = item.searched_at;
              return positions.push(item);
            }
            if (dateValue !== item.searched_at) {
              dateValue = item.searched_at;
              return positions.push(item);
            }
          });

          return positions;
        };

        if (this.selectedCompetition.length === 0) {
          this.$notify({
            group: "default",
            type: "info",
            text: this.$t("message.notification.noCompetitionAdded")
          });
        } else {
          const response = await serviceHandler(
            competitionService.getAvgCompetitionMapSearchResult(
              {
                businessAddress: this.selectedBusinessAddress.id,
                keyword: this.selectedKeywordId,
                competitionAddresses: this.selectedCompetition.map(
                  item => item.id
                )
              },
              {
                from: moment(this.currentMonth)
                  .startOf("month")
                  .format("YYYY-MM-DD"),
                to: moment(this.currentMonth)
                  .endOf("month")
                  .startOf("day")
                  .format("YYYY-MM-DD")
              }
            )
          );

          const businessAddressResults = createPositions(
            response.address[0].results
          );

          let competitionAdressesResults = [];

          if (response.competition_addresses.length > 0) {
            competitionAdressesResults = response.competition_addresses.map(
              result => {
                const positions = createPositions(result.results);
                const id = result.id;
                return { id, positions };
              }
            );
          }

          // Create businessAddress dataset
          if (businessAddressResults && businessAddressResults.length > 0) {
            businessAddressResults.forEach(item => {
              businessAddressData.data.push(item.position_organic);
              businessAddressLabels.push(item.searched_at);
            });
          }

          // Create competition addresses dataset
          if (competitionAdressesResults.length > 0) {
            competitionAdressesResults.map(results => {
              const data = [];

              results.positions.forEach(item => {
                data.push(item.position_organic);
              });

              const competition = this.selectedCompetition.find(
                item => item.id === results.id
              );

              return competitionData.push({
                address: `${competition.street}, ${competition.postal_code} ${competition.city}`,
                name: competition.name,
                color: competition.color,
                data
              });
            });
          }

          this.chartData = [businessAddressData, ...competitionData];
          this.chartLabels = businessAddressLabels;
        }

        resolve();
      });
    },

    handleImportBusinessAddresses() {
      gtmEvent__buttonClick({ label: "import_locations" });

      this.$router.push({ name: "profile-gmb-accounts" });
    },
    handleAddNewBusinessAddress() {
      gtmEvent__buttonClick({ label: "add_location" });

      this.$router.push({ name: "business-addresses" });
    },
    handleAddCompetitionBusinessAddress() {
      gtmEvent__buttonClick({ label: "add_competition_location" });

      this.$router.push({
        name: "competition-business-addresses",
        query: { itemId: this.selectedBusinessAddress.id }
      });
    },
    handleAddNewTerm() {
      gtmEvent__buttonClick({ label: "add_new_keyword" });

      this.$router.push({
        name: "profile-company-in-web"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.v-list-item--disabled {
  pointer-events: none;

  .v-list-item__action {
    pointer-events: auto;
  }
}

::v-deep .v-list-item.v-list-item--disabled {
  .v-list-item__subtitle {
    color: $gray-2 !important;
  }
}

.empty-list {
  @include font-size(13px);
  margin-bottom: $spacing * 2;
}
.tab-item {
  min-height: 800px;
  position: relative;
}

.select-hint__text {
  display: flex;
  align-items: center;
  margin-top: $spacing * -1.25;
}

.select-hint__text::before {
  content: "";
  display: inline-block;
  width: 20px;
  height: 20px;
  background: #f2ae01;
  margin-right: $spacing;
  border-radius: 3px;
}

.checkbox__number {
  height: 20px;
  width: 20px;
  border: 1px solid $orange;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: $orange;
  margin-left: $spacing * 0.5;
  margin-right: $spacing * 2.5;
}

.business-addresses__list {
  border: 1px solid rgba($dark, 0.05);
  border-radius: 1px;
}

.map-dynamic-legend {
  position: absolute;
  bottom: 0;
  padding: 0 $spacing;
}

.map-dynamic-legend__item {
  margin-bottom: $spacing;
}

.map-dynamic-legend__name {
  color: white;
}

.map-dynamic-legend__box {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: $gray;
  margin-right: $spacing;
  border-radius: 3px;
}

.no-data {
  height: 100%;
}

.p-relative {
  position: relative;
}

.import-btn {
  margin: 0 auto;
  max-width: 250px;
  width: 100%;
}
</style>
